import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Modal, Flex, Icon, Typo, theme } from '../../../ui';

const DropZone = styled.div`
  border: 2px dashed ${({ $hasFile }) => ($hasFile ? theme.colors.PRIMARY : theme.colors.NEU_200)};
  border-radius: 10px;
  padding: 32px 24px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  background: ${({ $hasFile }) => ($hasFile ? theme.colors.PRIMARY_EXTRA_LIGHT : theme.colors.NEU_0)};

  &:hover {
    border-color: ${theme.colors.PRIMARY};
    background: ${theme.colors.PRIMARY_EXTRA_LIGHT};
  }
`;

const FilePreview = styled(Flex)`
  padding: 14px 16px;
  background: ${theme.colors.NEU_50};
  border-radius: 8px;
  border: 1px solid ${theme.colors.NEU_100};
`;

const FileTypeIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: ${({ $type }) => {
    if ($type === 'pdf') return '#ffebee';
    if ($type === 'video') return '#e3f2fd';
    if ($type === 'doc') return '#e8f5e9';
    if ($type === 'ppt') return '#fff3e0';
    if ($type === 'image') return '#f3e5f5';
    return theme.colors.NEU_50;
  }};
  color: ${({ $type }) => {
    if ($type === 'pdf') return '#c62828';
    if ($type === 'video') return '#1565c0';
    if ($type === 'doc') return '#2e7d32';
    if ($type === 'ppt') return '#e65100';
    if ($type === 'image') return '#7b1fa2';
    return theme.colors.NEU_600;
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  flex-shrink: 0;
`;

const RemoveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: transparent;
  border: 1px solid ${theme.colors.NEU_200};
  border-radius: 6px;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.15s;
  &:hover {
    border-color: #c62828;
    background: #ffebee;
  }
`;

const HiddenInput = styled.input`
  display: none;
`;

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const detectFileType = (file) => {
  const ext = file.name.split('.').pop().toLowerCase();
  if (['pdf'].includes(ext)) return 'pdf';
  if (['doc', 'docx', 'txt', 'rtf', 'odt'].includes(ext)) return 'doc';
  if (['ppt', 'pptx', 'odp'].includes(ext)) return 'ppt';
  if (['mp4', 'mov', 'avi', 'webm', 'mkv'].includes(ext)) return 'video';
  if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(ext)) return 'image';
  return 'pdf';
};

const AddFileModal = ({ open, onClose, onSubmit }) => {
  const { t } = useTranslation();
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile({
        name: file.name,
        size: formatFileSize(file.size),
        type: detectFileType(file),
        rawSize: file.size,
      });
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      setSelectedFile({
        name: file.name,
        size: formatFileSize(file.size),
        type: detectFileType(file),
        rawSize: file.size,
      });
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleRemove = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleAccept = async () => {
    if (!selectedFile) return;
    await onSubmit({
      name: selectedFile.name,
      type: selectedFile.type,
      size: selectedFile.size,
    });
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('classroom.addFile', 'Add File to Library')}
      width='480px'
      showActions
      onAccept={handleAccept}
      acceptLabel={t('classroom.upload', 'Upload')}
      acceptIsDisabled={!selectedFile}
    >
      <Flex direction='column' gap='16'>
        <HiddenInput
          ref={fileInputRef}
          type='file'
          onChange={handleFileSelect}
          accept='.pdf,.doc,.docx,.ppt,.pptx,.mp4,.mov,.avi,.jpg,.jpeg,.png,.gif,.txt,.rtf'
        />

        {!selectedFile ? (
          <DropZone
            $hasFile={false}
            onClick={() => fileInputRef.current?.click()}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
          >
            <Flex direction='column' align='center' gap='10'>
              <Icon.Upload size='large' color={theme.colors.NEU_300} />
              <Typo.MediumText style={{ fontWeight: 500 }}>
                {t('classroom.dropFileHere', 'Drop a file here or click to browse')}
              </Typo.MediumText>
              <Typo.SmallText color={theme.colors.NEU_300}>
                {t('classroom.supportedFormats', 'PDF, DOC, PPT, Video, Images')}
              </Typo.SmallText>
            </Flex>
          </DropZone>
        ) : (
          <FilePreview align='center' gap='12'>
            <FileTypeIcon $type={selectedFile.type}>
              {selectedFile.type}
            </FileTypeIcon>
            <Flex direction='column' gap='2' style={{ flex: 1, minWidth: 0 }}>
              <Typo.MediumText style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {selectedFile.name}
              </Typo.MediumText>
              <Typo.SmallText color={theme.colors.NEU_300}>
                {selectedFile.size} · {selectedFile.type.toUpperCase()}
              </Typo.SmallText>
            </Flex>
            <RemoveButton onClick={handleRemove} title={t('classroom.removeFile', 'Remove')}>
              <Icon.Close size='x-small' color={theme.colors.NEU_300} />
            </RemoveButton>
          </FilePreview>
        )}
      </Flex>
    </Modal>
  );
};

export default AddFileModal;
